/**
 * Legal content — Terms of Service & Privacy Policy
 * =================================================
 *
 * Bilingual (EN/ES) source of truth for the /terms and /privacy pages.
 * Kept as structured data (not in i18n.ts) because the documents are long
 * and edited as whole sections rather than per-string.
 *
 * Positioning (must be preserved on edit): Hit the Land is an information /
 * decision-support source consulted as part of the customer's own due
 * diligence. It is NOT a certification body, accreditation authority, or
 * government agency, and it does not issue legally binding certifications or
 * guarantee compliance with EUDR or any other law. See the "Informational
 * nature" / "Naturaleza informativa" sections.
 *
 * Operator (legal entity): "Hit the Land" is a TRADE NAME only — no company is
 * incorporated. The Service is operated by Jean Paul Panchana Espinoza, a natural
 * person (persona natural) registered in Ecuador under RUC 0704333137001. See
 * OPERATOR_* constants below; this identity is shown in the footer and the docs.
 *
 * Governing law / jurisdiction: Republic of Ecuador; courts of the Operator's
 * domicile in Ecuador. Items still to confirm with legal counsel before scaling:
 *  - Exact registered address / canton (RUC 07 = El Oro province) for the venue clause
 *  - Whether legal@/privacy@hittheland.com mailboxes are live (else use a real address)
 *  - A full lawyer review of these drafts before any paid contract
 */

export const COMPANY_NAME = 'Hit the Land';
// "Hit the Land" is a commercial/trade name only — no company is incorporated yet.
// The Service is operated by a natural person (persona natural) registered in
// Ecuador. These details identify the legal operator behind the trade name and are
// shown in the footer and the legal documents.
export const OPERATOR_NAME = 'Jean Paul Panchana Espinoza';
export const OPERATOR_RUC = '0704333137001';
export const OPERATOR_COUNTRY = 'Ecuador';
export const LEGAL_CONTACT_EMAIL = 'legal@hittheland.com';
export const PRIVACY_CONTACT_EMAIL = 'privacy@hittheland.com';

/** ISO date the current legal texts took effect. Update when documents change. */
export const LEGAL_EFFECTIVE_DATE = '2026-06-27';

export type LegalBlock =
    | { type: 'p'; text: string }
    | { type: 'list'; items: string[] }
    | { type: 'callout'; text: string };

export interface LegalSection {
    heading: string;
    blocks: LegalBlock[];
}

export interface LegalDoc {
    title: string;
    intro: string;
    sections: LegalSection[];
}

export type Lang = 'en' | 'es';

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------

const termsEn: LegalDoc = {
    title: 'Terms of Service',
    intro:
        'These Terms of Service ("Terms") govern your access to and use of the Hit the Land platform and related services (the "Service"). By accessing or using the Service, or by creating an account, you agree to these Terms on behalf of yourself and the organization you represent. If you do not agree, do not use the Service.',
    sections: [
        {
            heading: 'About Hit the Land and the Service',
            blocks: [
                {
                    type: 'p',
                    text: '"Hit the Land" is a commercial name under which the Service is operated by Jean Paul Panchana Espinoza, a natural person (persona natural) registered for tax purposes in Ecuador under RUC 0704333137001 (the "Operator", "we", or "us"). "Hit the Land" and hittheland.com are trade names and do not designate a separately incorporated company; the Operator is the party that provides the Service and the entity responsible under these Terms.',
                },
                {
                    type: 'p',
                    text: 'Hit the Land provides a satellite-based decision-support and risk-screening platform for land use and deforestation monitoring. The Service ingests geospatial data (such as field or plot boundaries) and analyses Earth observation imagery — including the European Union Copernicus Sentinel-1 and Sentinel-2 missions — to produce risk indicators, analytical metrics, maps and reports intended to support due diligence relating to commodity supply chains and frameworks such as EU Regulation 2023/1115 (EUDR).',
                },
                {
                    type: 'callout',
                    text: 'The Service is an information source to be consulted as part of your own due diligence. Hit the Land is not a certification body, accreditation authority, or government agency. The Service does not issue legally binding certifications and does not guarantee compliance with any law, regulation, or governmental requirement. See "Informational nature — no certification" below.',
                },
            ],
        },
        {
            heading: 'Definitions',
            blocks: [
                {
                    type: 'list',
                    items: [
                        '"Customer" or "you" — the organization on whose behalf the Service is accessed and used.',
                        '"User" — an individual authorized to access the Service under a Customer account.',
                        '"Customer Data" — geospatial data, metadata, files and other content you upload to or generate through the Service.',
                        '"Output" — the risk indicators, analyses, maps, reports and other results produced by the Service.',
                        '"Documentation" — the usage materials Hit the Land makes available for the Service.',
                    ],
                },
            ],
        },
        {
            heading: 'Informational nature — no certification or compliance guarantee',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service provides decision-support information derived from automated analysis of third-party Earth observation data. Any Output — including fields labelled "compliance status", "risk level", "certificate", or similar — is an analytical estimate provided for informational and due-diligence-support purposes only.',
                },
                {
                    type: 'list',
                    items: [
                        'The Service does not constitute legal, regulatory, financial, or other professional advice.',
                        'Outputs do not replace mandatory regulatory declarations, official government determinations, accredited third-party certifications, field audits, or independent verification.',
                        'Hit the Land does not warrant that use of the Service will result in compliance with the EUDR or any other law or standard, and makes no representation that any authority or counterparty will accept the Outputs.',
                        'You remain solely responsible for your regulatory obligations and for any decision you make in reliance on the Outputs.',
                    ],
                },
                {
                    type: 'p',
                    text: 'Earth observation analysis is inherently subject to limitations, including cloud cover, sensor resolution, satellite revisit frequency, atmospheric conditions, and the availability and quality of third-party data. Outputs may contain errors, gaps, or uncertainty and should be corroborated with independent sources before any material decision.',
                },
            ],
        },
        {
            heading: 'Eligibility and accounts',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service is intended for business and professional use by organizations, not for consumers. You must be at least 18 years old and authorized to bind your organization to these Terms.',
                },
                {
                    type: 'list',
                    items: [
                        'You are responsible for keeping your credentials confidential and for all activity under your account.',
                        'Accounts are organized under a tenant (organization) with role-based access; you are responsible for managing your Users and their roles.',
                        'You must ensure your Users comply with these Terms and must promptly notify us of any unauthorized use of your account.',
                    ],
                },
            ],
        },
        {
            heading: 'Customer Data and licence',
            blocks: [
                {
                    type: 'p',
                    text: 'As between the parties, you retain all rights in Customer Data. You grant Hit the Land a worldwide, non-exclusive licence to host, process, transmit, and analyse Customer Data solely to provide and maintain the Service, to generate Outputs, and as otherwise permitted in these Terms and the Privacy Policy.',
                },
                {
                    type: 'list',
                    items: [
                        'You represent that you have all rights and authority necessary to upload Customer Data and to permit its processing, including any third-party or personal data it contains (for example, producer names or plot locations).',
                        'You are responsible for the accuracy, quality, and legality of Customer Data; the quality of Outputs depends on the inputs you provide.',
                        'Where Customer Data includes personal data, the parties’ respective roles and obligations are described in the Privacy Policy and, where applicable, a separate Data Processing Agreement.',
                    ],
                },
            ],
        },
        {
            heading: 'Acceptable use',
            blocks: [
                { type: 'p', text: 'You agree not to:' },
                {
                    type: 'list',
                    items: [
                        'use the Service in violation of any applicable law or third-party right;',
                        'upload data you are not authorized to share;',
                        'attempt to gain unauthorized access to the Service or to other tenants’ data;',
                        'reverse engineer, decompile, or attempt to extract the underlying models or methodology, except to the extent this restriction is prohibited by law;',
                        'interfere with or disrupt the integrity or performance of the Service, or circumvent usage limits or security controls;',
                        'resell, sublicense, or make the Service available to third parties except as expressly agreed in writing.',
                    ],
                },
            ],
        },
        {
            heading: 'Intellectual property',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service — including the platform, software, analytical methodology, models, and Documentation — and all related intellectual property are owned by Hit the Land or its licensors. Except for the limited rights expressly granted to use the Service and Outputs for your internal business purposes, no rights are transferred to you.',
                },
                {
                    type: 'p',
                    text: 'You may use Outputs for your internal business and due-diligence purposes and may share individual reports with your supply-chain counterparties and competent authorities, provided you do not remove the disclaimers and do not misrepresent the nature of the Outputs.',
                },
            ],
        },
        {
            heading: 'Third-party data and attribution',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service relies on third-party Earth observation data and infrastructure, including the Copernicus programme of the European Union and the European Space Agency (Sentinel-1 and Sentinel-2), and catalogue and processing providers such as Element 84 and the Microsoft Planetary Computer. Such data is subject to the providers’ own terms and licences.',
                },
                {
                    type: 'p',
                    text: 'Hit the Land does not control, and is not responsible for, the availability, accuracy, or continuity of third-party data sources, and may substitute or modify data sources at any time.',
                },
            ],
        },
        {
            heading: 'Service availability, changes, and beta features',
            blocks: [
                {
                    type: 'p',
                    text: 'We aim to keep the Service available but do not guarantee uninterrupted or error-free operation. We may modify, suspend, or discontinue features at any time. Pre-release or "beta" features are provided "as is", without warranty, and may be changed or withdrawn.',
                },
            ],
        },
        {
            heading: 'Fees',
            blocks: [
                {
                    type: 'p',
                    text: 'Where the Service is provided under a paid subscription or order, the applicable fees, billing, and payment terms are set out in the relevant order form or written agreement between you and Hit the Land, which supplements these Terms.',
                },
            ],
        },
        {
            heading: 'Confidentiality',
            blocks: [
                {
                    type: 'p',
                    text: 'Each party may receive confidential information of the other. The receiving party will protect such information with reasonable care and use it only to exercise its rights and perform its obligations under these Terms, except where disclosure is required by law.',
                },
            ],
        },
        {
            heading: 'Disclaimer of warranties',
            blocks: [
                {
                    type: 'p',
                    text: 'To the maximum extent permitted by law, the Service and Outputs are provided "as is" and "as available", without warranties of any kind, whether express, implied, or statutory, including any implied warranties of merchantability, fitness for a particular purpose, accuracy, or non-infringement. Hit the Land does not warrant that the Outputs are accurate, complete, current, or suitable for any particular regulatory or commercial purpose.',
                },
            ],
        },
        {
            heading: 'Limitation of liability',
            blocks: [
                {
                    type: 'p',
                    text: 'To the maximum extent permitted by law, Hit the Land and its affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or goodwill, arising out of or relating to the Service or Outputs, even if advised of the possibility of such damages.',
                },
                {
                    type: 'p',
                    text: 'To the maximum extent permitted by law, Hit the Land’s aggregate liability arising out of or relating to the Service will not exceed the amounts you paid for the Service in the twelve (12) months preceding the event giving rise to the claim (or, where no fees were paid, EUR 100). Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law.',
                },
            ],
        },
        {
            heading: 'Indemnification',
            blocks: [
                {
                    type: 'p',
                    text: 'You will defend and indemnify Hit the Land against third-party claims arising from your Customer Data, your use of the Service in breach of these Terms, or your violation of applicable law or third-party rights.',
                },
            ],
        },
        {
            heading: 'Term and termination',
            blocks: [
                {
                    type: 'p',
                    text: 'These Terms apply while you access or use the Service. Either party may terminate as set out in the applicable order form or, absent one, on reasonable notice. We may suspend or terminate access for breach of these Terms or where necessary to protect the Service or other users. On termination, your right to use the Service ends; you may request export or deletion of Customer Data as described in the Privacy Policy and any applicable agreement, subject to legal retention requirements.',
                },
            ],
        },
        {
            heading: 'Governing law and disputes',
            blocks: [
                {
                    type: 'p',
                    text: 'These Terms are governed by the laws of the Republic of Ecuador, excluding its conflict-of-laws rules. The parties submit to the exclusive jurisdiction of the competent courts of the Operator’s domicile in Ecuador, without prejudice to any mandatory protections available to you under the law of your place of residence or establishment.',
                },
            ],
        },
        {
            heading: 'Changes to these Terms',
            blocks: [
                {
                    type: 'p',
                    text: 'We may update these Terms from time to time. We will notify material changes through the Service or by other reasonable means and will revise the "last updated" date. Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms.',
                },
            ],
        },
        {
            heading: 'Contact',
            blocks: [
                {
                    type: 'p',
                    text: 'Questions about these Terms can be sent to ' + LEGAL_CONTACT_EMAIL + '.',
                },
            ],
        },
    ],
};

const termsEs: LegalDoc = {
    title: 'Términos del Servicio',
    intro:
        'Estos Términos del Servicio ("Términos") regulan tu acceso y uso de la plataforma Hit the Land y los servicios relacionados (el "Servicio"). Al acceder o usar el Servicio, o al crear una cuenta, aceptas estos Términos en tu nombre y en el de la organización que representas. Si no estás de acuerdo, no uses el Servicio.',
    sections: [
        {
            heading: 'Acerca de Hit the Land y del Servicio',
            blocks: [
                {
                    type: 'p',
                    text: '"Hit the Land" es un nombre comercial bajo el cual el Servicio es operado por Jean Paul Panchana Espinoza, persona natural inscrita en el Ecuador con RUC 0704333137001 (el "Operador", "nosotros"). "Hit the Land" y hittheland.com son nombres comerciales y no designan una compañía constituida por separado; el Operador es quien presta el Servicio y la persona responsable conforme a estos Términos.',
                },
                {
                    type: 'p',
                    text: 'Hit the Land ofrece una plataforma de apoyo a la decisión y de screening de riesgo basada en imágenes satelitales para el monitoreo del uso del suelo y de la deforestación. El Servicio procesa datos geoespaciales (como los límites de lotes o parcelas) y analiza imágenes de observación de la Tierra —incluidas las misiones Sentinel-1 y Sentinel-2 del programa Copernicus de la Unión Europea— para generar indicadores de riesgo, métricas analíticas, mapas e informes destinados a apoyar la debida diligencia en cadenas de suministro de commodities y marcos como el Reglamento UE 2023/1115 (EUDR).',
                },
                {
                    type: 'callout',
                    text: 'El Servicio es una fuente de información que debe consultarse como parte de tu propia debida diligencia. Hit the Land no es un organismo de certificación, una autoridad de acreditación ni una entidad gubernamental. El Servicio no emite certificaciones jurídicamente vinculantes ni garantiza el cumplimiento de ninguna ley, reglamento o requisito gubernamental. Ver "Naturaleza informativa — sin certificación" más abajo.',
                },
            ],
        },
        {
            heading: 'Definiciones',
            blocks: [
                {
                    type: 'list',
                    items: [
                        '"Cliente" o "tú" — la organización en cuyo nombre se accede y usa el Servicio.',
                        '"Usuario" — una persona autorizada a acceder al Servicio bajo una cuenta de Cliente.',
                        '"Datos del Cliente" — los datos geoespaciales, metadatos, archivos y demás contenido que cargas o generas a través del Servicio.',
                        '"Resultados" — los indicadores de riesgo, análisis, mapas, informes y demás resultados producidos por el Servicio.',
                        '"Documentación" — los materiales de uso que Hit the Land pone a disposición para el Servicio.',
                    ],
                },
            ],
        },
        {
            heading: 'Naturaleza informativa — sin certificación ni garantía de cumplimiento',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio proporciona información de apoyo a la decisión derivada del análisis automatizado de datos de observación de la Tierra de terceros. Cualquier Resultado —incluidos los campos denominados "estado de cumplimiento", "nivel de riesgo", "certificado" o similares— es una estimación analítica proporcionada únicamente con fines informativos y de apoyo a la debida diligencia.',
                },
                {
                    type: 'list',
                    items: [
                        'El Servicio no constituye asesoramiento legal, regulatorio, financiero ni profesional de ningún tipo.',
                        'Los Resultados no sustituyen las declaraciones regulatorias obligatorias, las determinaciones oficiales de las autoridades, las certificaciones acreditadas de terceros, las auditorías de campo ni la verificación independiente.',
                        'Hit the Land no garantiza que el uso del Servicio resulte en el cumplimiento del EUDR ni de ninguna otra ley o estándar, y no asegura que ninguna autoridad o contraparte vaya a aceptar los Resultados.',
                        'Eres el único responsable de tus obligaciones regulatorias y de cualquier decisión que tomes confiando en los Resultados.',
                    ],
                },
                {
                    type: 'p',
                    text: 'El análisis de observación de la Tierra está sujeto a limitaciones inherentes, como la cobertura de nubes, la resolución del sensor, la frecuencia de revisita del satélite, las condiciones atmosféricas y la disponibilidad y calidad de los datos de terceros. Los Resultados pueden contener errores, vacíos o incertidumbre y deben corroborarse con fuentes independientes antes de cualquier decisión relevante.',
                },
            ],
        },
        {
            heading: 'Elegibilidad y cuentas',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio está destinado al uso empresarial y profesional por parte de organizaciones, no a consumidores. Debes ser mayor de 18 años y estar autorizado para obligar a tu organización a estos Términos.',
                },
                {
                    type: 'list',
                    items: [
                        'Eres responsable de mantener la confidencialidad de tus credenciales y de toda la actividad realizada bajo tu cuenta.',
                        'Las cuentas se organizan bajo un tenant (organización) con acceso por roles; eres responsable de gestionar a tus Usuarios y sus roles.',
                        'Debes asegurarte de que tus Usuarios cumplan estos Términos y notificarnos de inmediato cualquier uso no autorizado de tu cuenta.',
                    ],
                },
            ],
        },
        {
            heading: 'Datos del Cliente y licencia',
            blocks: [
                {
                    type: 'p',
                    text: 'Entre las partes, conservas todos los derechos sobre los Datos del Cliente. Otorgas a Hit the Land una licencia mundial y no exclusiva para alojar, procesar, transmitir y analizar los Datos del Cliente con el único fin de prestar y mantener el Servicio, generar los Resultados y según lo permitido en estos Términos y en la Política de Privacidad.',
                },
                {
                    type: 'list',
                    items: [
                        'Declaras que cuentas con todos los derechos y la autoridad necesarios para cargar los Datos del Cliente y permitir su procesamiento, incluidos los datos de terceros o personales que contengan (por ejemplo, nombres de productores o ubicaciones de parcelas).',
                        'Eres responsable de la exactitud, calidad y legalidad de los Datos del Cliente; la calidad de los Resultados depende de los datos que proporciones.',
                        'Cuando los Datos del Cliente incluyan datos personales, los roles y obligaciones respectivos de las partes se describen en la Política de Privacidad y, cuando corresponda, en un Acuerdo de Tratamiento de Datos independiente.',
                    ],
                },
            ],
        },
        {
            heading: 'Uso aceptable',
            blocks: [
                { type: 'p', text: 'Te comprometes a no:' },
                {
                    type: 'list',
                    items: [
                        'usar el Servicio en violación de cualquier ley aplicable o derecho de terceros;',
                        'cargar datos que no estés autorizado a compartir;',
                        'intentar obtener acceso no autorizado al Servicio o a los datos de otros tenants;',
                        'realizar ingeniería inversa, descompilar o intentar extraer los modelos o la metodología subyacentes, salvo en la medida en que esta restricción esté prohibida por la ley;',
                        'interferir o alterar la integridad o el rendimiento del Servicio, ni eludir los límites de uso o los controles de seguridad;',
                        'revender, sublicenciar o poner el Servicio a disposición de terceros salvo acuerdo expreso por escrito.',
                    ],
                },
            ],
        },
        {
            heading: 'Propiedad intelectual',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio —incluidos la plataforma, el software, la metodología analítica, los modelos y la Documentación— y toda la propiedad intelectual relacionada pertenecen a Hit the Land o a sus licenciantes. Salvo por los derechos limitados expresamente otorgados para usar el Servicio y los Resultados con fines internos de tu negocio, no se te transfiere ningún derecho.',
                },
                {
                    type: 'p',
                    text: 'Puedes usar los Resultados para tus fines internos de negocio y de debida diligencia, y compartir informes individuales con tus contrapartes de la cadena de suministro y con las autoridades competentes, siempre que no elimines los avisos legales ni tergiverses la naturaleza de los Resultados.',
                },
            ],
        },
        {
            heading: 'Datos de terceros y atribución',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio depende de datos e infraestructura de observación de la Tierra de terceros, incluidos el programa Copernicus de la Unión Europea y la Agencia Espacial Europea (Sentinel-1 y Sentinel-2), y proveedores de catálogo y procesamiento como Element 84 y el Microsoft Planetary Computer. Dichos datos están sujetos a los términos y licencias de los propios proveedores.',
                },
                {
                    type: 'p',
                    text: 'Hit the Land no controla, ni es responsable de, la disponibilidad, exactitud o continuidad de las fuentes de datos de terceros, y puede sustituir o modificar dichas fuentes en cualquier momento.',
                },
            ],
        },
        {
            heading: 'Disponibilidad del Servicio, cambios y funciones beta',
            blocks: [
                {
                    type: 'p',
                    text: 'Procuramos mantener el Servicio disponible, pero no garantizamos un funcionamiento ininterrumpido ni libre de errores. Podemos modificar, suspender o discontinuar funciones en cualquier momento. Las funciones en versión preliminar o "beta" se ofrecen "tal cual", sin garantía, y pueden cambiarse o retirarse.',
                },
            ],
        },
        {
            heading: 'Tarifas',
            blocks: [
                {
                    type: 'p',
                    text: 'Cuando el Servicio se preste bajo una suscripción u orden de pago, las tarifas, la facturación y las condiciones de pago aplicables se establecen en el formulario de pedido o el acuerdo escrito correspondiente entre tú y Hit the Land, que complementa estos Términos.',
                },
            ],
        },
        {
            heading: 'Confidencialidad',
            blocks: [
                {
                    type: 'p',
                    text: 'Cada parte puede recibir información confidencial de la otra. La parte receptora la protegerá con un cuidado razonable y la usará únicamente para ejercer sus derechos y cumplir sus obligaciones bajo estos Términos, salvo cuando la divulgación sea requerida por ley.',
                },
            ],
        },
        {
            heading: 'Exención de garantías',
            blocks: [
                {
                    type: 'p',
                    text: 'En la máxima medida permitida por la ley, el Servicio y los Resultados se proporcionan "tal cual" y "según disponibilidad", sin garantías de ningún tipo, ya sean expresas, implícitas o legales, incluidas las garantías implícitas de comerciabilidad, idoneidad para un fin determinado, exactitud o no infracción. Hit the Land no garantiza que los Resultados sean exactos, completos, actuales o adecuados para ningún fin regulatorio o comercial concreto.',
                },
            ],
        },
        {
            heading: 'Limitación de responsabilidad',
            blocks: [
                {
                    type: 'p',
                    text: 'En la máxima medida permitida por la ley, Hit the Land y sus afiliadas no serán responsables de daños indirectos, incidentales, especiales, consecuentes o punitivos, ni de pérdida de beneficios, ingresos, datos o fondo de comercio, derivados o relacionados con el Servicio o los Resultados, incluso si se advirtió de su posibilidad.',
                },
                {
                    type: 'p',
                    text: 'En la máxima medida permitida por la ley, la responsabilidad total de Hit the Land derivada o relacionada con el Servicio no excederá los importes que hayas pagado por el Servicio en los doce (12) meses anteriores al hecho que origine la reclamación (o, si no se pagaron tarifas, 100 EUR). Nada en estos Términos excluye o limita la responsabilidad que no pueda excluirse o limitarse conforme a la ley aplicable.',
                },
            ],
        },
        {
            heading: 'Indemnización',
            blocks: [
                {
                    type: 'p',
                    text: 'Defenderás e indemnizarás a Hit the Land frente a reclamaciones de terceros derivadas de tus Datos del Cliente, de tu uso del Servicio en incumplimiento de estos Términos, o de tu violación de la ley aplicable o de derechos de terceros.',
                },
            ],
        },
        {
            heading: 'Vigencia y terminación',
            blocks: [
                {
                    type: 'p',
                    text: 'Estos Términos aplican mientras accedas o uses el Servicio. Cualquiera de las partes puede terminar según lo establecido en el formulario de pedido aplicable o, en su defecto, con un preaviso razonable. Podemos suspender o terminar el acceso por incumplimiento de estos Términos o cuando sea necesario para proteger el Servicio u otros usuarios. Al terminar, cesa tu derecho a usar el Servicio; puedes solicitar la exportación o eliminación de los Datos del Cliente según se describe en la Política de Privacidad y en el acuerdo aplicable, sujeto a los requisitos legales de conservación.',
                },
            ],
        },
        {
            heading: 'Ley aplicable y controversias',
            blocks: [
                {
                    type: 'p',
                    text: 'Estos Términos se rigen por las leyes de la República del Ecuador, excluyendo sus normas de conflicto de leyes. Las partes se someten a la jurisdicción exclusiva de los tribunales competentes del domicilio del Operador en el Ecuador, sin perjuicio de las protecciones imperativas que te reconozca la ley de tu lugar de residencia o establecimiento.',
                },
            ],
        },
        {
            heading: 'Cambios a estos Términos',
            blocks: [
                {
                    type: 'p',
                    text: 'Podemos actualizar estos Términos periódicamente. Notificaremos los cambios sustanciales a través del Servicio o por otros medios razonables y actualizaremos la fecha de "última actualización". El uso continuado del Servicio tras la entrada en vigor de los cambios constituye la aceptación de los Términos actualizados.',
                },
            ],
        },
        {
            heading: 'Contacto',
            blocks: [
                {
                    type: 'p',
                    text: 'Las consultas sobre estos Términos pueden enviarse a ' + LEGAL_CONTACT_EMAIL + '.',
                },
            ],
        },
    ],
};

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------

const privacyEn: LegalDoc = {
    title: 'Privacy Policy',
    intro:
        'This Privacy Policy explains how Hit the Land ("Hit the Land", "we", "us") collects, uses, and protects personal data in connection with the Hit the Land platform and related services (the "Service"). It applies to our customers, their authorized users, and visitors to our sign-in pages.',
    sections: [
        {
            heading: 'Who we are and our role',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service ("Hit the Land") is operated by Jean Paul Panchana Espinoza, a natural person registered in Ecuador (RUC 0704333137001), who provides this satellite-based decision-support and risk-screening platform. We act as a data controller for the account, authentication, and usage data of users who access the Service and for visitors to our public pages. For the geospatial content and related data that a customer uploads and processes through the Service, the customer is the controller and Hit the Land acts as a processor on the customer’s behalf, governed by the applicable agreement and, where required, a Data Processing Agreement.',
                },
            ],
        },
        {
            heading: 'Data we collect',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'Account and identity data: name, email address, organization name, role, and language preference.',
                        'Authentication data: hashed passwords and session and security tokens.',
                        'Customer content: geospatial boundaries (polygons) and associated metadata you upload or generate, which may include third-party information such as producer or plot identifiers and locations.',
                        'Outputs: the analyses, risk indicators, maps, and reports generated from your content.',
                        'Usage and technical data: IP address, device and browser information, log and audit records, and actions taken within the Service.',
                        'Cookies strictly necessary for authentication and security (see "Cookies" below).',
                    ],
                },
            ],
        },
        {
            heading: 'How we use data',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'to provide, operate, and secure the Service;',
                        'to run analyses and generate Outputs;',
                        'to authenticate users and maintain separation between tenants;',
                        'to keep security and audit logs and to prevent abuse;',
                        'to provide support and communicate with you about the Service;',
                        'to maintain, improve, and develop the Service;',
                        'to comply with our legal obligations.',
                    ],
                },
                {
                    type: 'p',
                    text: 'We do not sell personal data, and we do not use it for third-party advertising.',
                },
            ],
        },
        {
            heading: 'Legal bases (where the GDPR, the Ecuadorian LOPDP, or similar laws apply)',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'Performance of a contract — to provide the Service to you and your organization.',
                        'Our legitimate interests — to secure, maintain, and improve the Service and prevent abuse, balanced against your rights and freedoms.',
                        'Compliance with legal obligations.',
                        'Consent, where required — which you may withdraw at any time.',
                    ],
                },
            ],
        },
        {
            heading: 'Cookies and similar technologies',
            blocks: [
                {
                    type: 'p',
                    text: 'We use strictly necessary cookies to operate the Service, including an authentication session cookie and a CSRF-protection token cookie. We do not use advertising or cross-site tracking cookies. Because these cookies are essential to deliver a service you request, they do not require consent under most frameworks; disabling them will prevent you from signing in.',
                },
            ],
        },
        {
            heading: 'Sharing and sub-processors',
            blocks: [
                { type: 'p', text: 'We share data only as needed to provide the Service:' },
                {
                    type: 'list',
                    items: [
                        'Cloud infrastructure and storage providers (for example, Amazon Web Services) that host the platform and store files and reports.',
                        'Earth observation data and processing providers (for example, the Copernicus programme / European Space Agency, Element 84, and the Microsoft Planetary Computer) used to retrieve satellite imagery; for these we send query parameters such as area-of-interest geometry and dates, not your account credentials.',
                        'Service providers supporting communications, error monitoring, and similar operational functions, under appropriate confidentiality and data-protection terms.',
                    ],
                },
                {
                    type: 'p',
                    text: 'We may also disclose data where required by law or to protect rights, safety, and the integrity of the Service.',
                },
            ],
        },
        {
            heading: 'International transfers',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service is hosted on cloud infrastructure that may be located outside your country, including in the United States. Where personal data is transferred internationally, we rely on appropriate safeguards such as the European Commission’s Standard Contractual Clauses or an equivalent mechanism.',
                },
            ],
        },
        {
            heading: 'Data retention',
            blocks: [
                {
                    type: 'p',
                    text: 'We retain account and authentication data for as long as your account is active and as needed to provide the Service. Customer content and Outputs are retained according to your configuration and the applicable agreement; generated reports may carry a defined retention or expiry period. Security and audit logs are retained for a limited period for security and legal purposes. We delete or anonymize data when it is no longer needed, subject to legal retention requirements.',
                },
            ],
        },
        {
            heading: 'Security',
            blocks: [
                {
                    type: 'p',
                    text: 'We apply technical and organizational measures appropriate to the risk, including encryption in transit, hashed credentials, token-based authentication with role-based access control, and logical separation between tenants. No system is completely secure, and we cannot guarantee absolute security.',
                },
            ],
        },
        {
            heading: 'Your rights',
            blocks: [
                {
                    type: 'p',
                    text: 'Subject to applicable law, you may have the right to access, rectify, erase, restrict, or object to the processing of your personal data, the right to data portability, and the right to withdraw consent. You may also lodge a complaint with your data protection authority (in Ecuador, the Superintendencia de Protección de Datos Personales).',
                },
                {
                    type: 'p',
                    text: 'To exercise these rights, contact ' + PRIVACY_CONTACT_EMAIL + '. Where Hit the Land processes data on a customer’s behalf (customer content), we will refer requests to the relevant customer (the controller) and support them in responding.',
                },
            ],
        },
        {
            heading: 'Personal data within customer content',
            blocks: [
                {
                    type: 'p',
                    text: 'Customer content may include personal data of third parties, such as producers or landowners. The customer is responsible, as controller, for having a lawful basis to upload and process such data, for providing any required notices to those individuals, and for the accuracy of that data. Hit the Land processes it only to provide the Service.',
                },
            ],
        },
        {
            heading: 'Children',
            blocks: [
                {
                    type: 'p',
                    text: 'The Service is intended for business use and is not directed to children. We do not knowingly collect personal data from children.',
                },
            ],
        },
        {
            heading: 'Changes to this Policy',
            blocks: [
                {
                    type: 'p',
                    text: 'We may update this Policy from time to time. We will notify material changes through the Service or by other reasonable means and will revise the "last updated" date.',
                },
            ],
        },
        {
            heading: 'Contact',
            blocks: [
                {
                    type: 'p',
                    text: 'For privacy questions or to exercise your rights, contact ' + PRIVACY_CONTACT_EMAIL + '.',
                },
            ],
        },
    ],
};

const privacyEs: LegalDoc = {
    title: 'Política de Privacidad',
    intro:
        'Esta Política de Privacidad explica cómo Hit the Land ("Hit the Land", "nosotros") recopila, usa y protege los datos personales en relación con la plataforma Hit the Land y los servicios relacionados (el "Servicio"). Se aplica a nuestros clientes, a sus usuarios autorizados y a los visitantes de nuestras páginas de inicio de sesión.',
    sections: [
        {
            heading: 'Quiénes somos y nuestro rol',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio ("Hit the Land") es operado por Jean Paul Panchana Espinoza, persona natural inscrita en el Ecuador (RUC 0704333137001), quien ofrece esta plataforma de apoyo a la decisión y de screening de riesgo basada en imágenes satelitales. Actuamos como responsable del tratamiento (controller) de los datos de cuenta, autenticación y uso de los usuarios que acceden al Servicio y de los visitantes de nuestras páginas públicas. Respecto del contenido geoespacial y los datos relacionados que un cliente carga y procesa a través del Servicio, el cliente es el responsable del tratamiento y Hit the Land actúa como encargado del tratamiento (processor) por cuenta del cliente, conforme al acuerdo aplicable y, cuando corresponda, a un Acuerdo de Tratamiento de Datos.',
                },
            ],
        },
        {
            heading: 'Datos que recopilamos',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'Datos de cuenta e identidad: nombre, correo electrónico, nombre de la organización, rol y preferencia de idioma.',
                        'Datos de autenticación: contraseñas con hash y tokens de sesión y seguridad.',
                        'Contenido del cliente: límites geoespaciales (polígonos) y metadatos asociados que cargas o generas, que pueden incluir información de terceros como identificadores o ubicaciones de productores o parcelas.',
                        'Resultados: los análisis, indicadores de riesgo, mapas e informes generados a partir de tu contenido.',
                        'Datos técnicos y de uso: dirección IP, información del dispositivo y del navegador, registros de actividad y auditoría, y acciones realizadas dentro del Servicio.',
                        'Cookies estrictamente necesarias para la autenticación y la seguridad (ver "Cookies" más abajo).',
                    ],
                },
            ],
        },
        {
            heading: 'Cómo usamos los datos',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'para prestar, operar y asegurar el Servicio;',
                        'para ejecutar análisis y generar Resultados;',
                        'para autenticar a los usuarios y mantener la separación entre tenants;',
                        'para mantener registros de seguridad y auditoría y prevenir abusos;',
                        'para brindar soporte y comunicarnos contigo sobre el Servicio;',
                        'para mantener, mejorar y desarrollar el Servicio;',
                        'para cumplir nuestras obligaciones legales.',
                    ],
                },
                {
                    type: 'p',
                    text: 'No vendemos datos personales ni los usamos para publicidad de terceros.',
                },
            ],
        },
        {
            heading: 'Bases legales (cuando aplica el RGPD, la LOPDP del Ecuador o leyes similares)',
            blocks: [
                {
                    type: 'list',
                    items: [
                        'Ejecución de un contrato — para prestar el Servicio a ti y a tu organización.',
                        'Nuestros intereses legítimos — para asegurar, mantener y mejorar el Servicio y prevenir abusos, ponderados frente a tus derechos y libertades.',
                        'Cumplimiento de obligaciones legales.',
                        'Consentimiento, cuando sea necesario — que puedes retirar en cualquier momento.',
                    ],
                },
            ],
        },
        {
            heading: 'Cookies y tecnologías similares',
            blocks: [
                {
                    type: 'p',
                    text: 'Usamos cookies estrictamente necesarias para operar el Servicio, incluida una cookie de sesión de autenticación y una cookie de token de protección CSRF. No usamos cookies de publicidad ni de seguimiento entre sitios. Dado que estas cookies son esenciales para prestar un servicio que solicitas, no requieren consentimiento en la mayoría de los marcos; deshabilitarlas impedirá que puedas iniciar sesión.',
                },
            ],
        },
        {
            heading: 'Divulgación y encargados (sub-processors)',
            blocks: [
                { type: 'p', text: 'Compartimos datos solo en la medida necesaria para prestar el Servicio:' },
                {
                    type: 'list',
                    items: [
                        'Proveedores de infraestructura cloud y almacenamiento (por ejemplo, Amazon Web Services) que alojan la plataforma y almacenan archivos e informes.',
                        'Proveedores de datos y procesamiento de observación de la Tierra (por ejemplo, el programa Copernicus / Agencia Espacial Europea, Element 84 y el Microsoft Planetary Computer) utilizados para obtener imágenes satelitales; a estos les enviamos parámetros de consulta como la geometría del área de interés y las fechas, no tus credenciales de cuenta.',
                        'Proveedores de servicios que apoyan comunicaciones, monitoreo de errores y funciones operativas similares, bajo términos adecuados de confidencialidad y protección de datos.',
                    ],
                },
                {
                    type: 'p',
                    text: 'También podemos divulgar datos cuando lo exija la ley o para proteger derechos, la seguridad y la integridad del Servicio.',
                },
            ],
        },
        {
            heading: 'Transferencias internacionales',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio se aloja en infraestructura cloud que puede estar ubicada fuera de tu país, incluidos los Estados Unidos. Cuando se transfieren datos personales a nivel internacional, nos apoyamos en salvaguardas adecuadas, como las Cláusulas Contractuales Tipo de la Comisión Europea o un mecanismo equivalente.',
                },
            ],
        },
        {
            heading: 'Conservación de datos',
            blocks: [
                {
                    type: 'p',
                    text: 'Conservamos los datos de cuenta y autenticación mientras tu cuenta esté activa y según sea necesario para prestar el Servicio. El contenido del cliente y los Resultados se conservan según tu configuración y el acuerdo aplicable; los informes generados pueden tener un período definido de conservación o expiración. Los registros de seguridad y auditoría se conservan por un período limitado con fines de seguridad y legales. Eliminamos o anonimizamos los datos cuando ya no son necesarios, sujeto a los requisitos legales de conservación.',
                },
            ],
        },
        {
            heading: 'Seguridad',
            blocks: [
                {
                    type: 'p',
                    text: 'Aplicamos medidas técnicas y organizativas adecuadas al riesgo, incluidas el cifrado en tránsito, las credenciales con hash, la autenticación basada en tokens con control de acceso por roles y la separación lógica entre tenants. Ningún sistema es completamente seguro y no podemos garantizar una seguridad absoluta.',
                },
            ],
        },
        {
            heading: 'Tus derechos',
            blocks: [
                {
                    type: 'p',
                    text: 'Sujeto a la ley aplicable, puedes tener derecho a acceder, rectificar, suprimir, limitar u oponerte al tratamiento de tus datos personales, a la portabilidad de los datos y a retirar el consentimiento. También puedes presentar una reclamación ante tu autoridad de protección de datos (en Ecuador, la Superintendencia de Protección de Datos Personales).',
                },
                {
                    type: 'p',
                    text: 'Para ejercer estos derechos, escribe a ' + PRIVACY_CONTACT_EMAIL + '. Cuando Hit the Land trate datos por cuenta de un cliente (contenido del cliente), remitiremos las solicitudes al cliente correspondiente (el responsable del tratamiento) y le daremos apoyo para responder.',
                },
            ],
        },
        {
            heading: 'Datos personales dentro del contenido del cliente',
            blocks: [
                {
                    type: 'p',
                    text: 'El contenido del cliente puede incluir datos personales de terceros, como productores o propietarios de tierras. El cliente es responsable, como responsable del tratamiento, de contar con una base legal para cargar y procesar dichos datos, de proporcionar los avisos requeridos a esas personas y de la exactitud de esos datos. Hit the Land los trata únicamente para prestar el Servicio.',
                },
            ],
        },
        {
            heading: 'Menores',
            blocks: [
                {
                    type: 'p',
                    text: 'El Servicio está destinado al uso empresarial y no se dirige a menores. No recopilamos a sabiendas datos personales de menores.',
                },
            ],
        },
        {
            heading: 'Cambios a esta Política',
            blocks: [
                {
                    type: 'p',
                    text: 'Podemos actualizar esta Política periódicamente. Notificaremos los cambios sustanciales a través del Servicio o por otros medios razonables y actualizaremos la fecha de "última actualización".',
                },
            ],
        },
        {
            heading: 'Contacto',
            blocks: [
                {
                    type: 'p',
                    text: 'Para consultas de privacidad o para ejercer tus derechos, escribe a ' + PRIVACY_CONTACT_EMAIL + '.',
                },
            ],
        },
    ],
};

export const termsContent: Record<Lang, LegalDoc> = { en: termsEn, es: termsEs };
export const privacyContent: Record<Lang, LegalDoc> = { en: privacyEn, es: privacyEs };
